import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Grid,
  useTheme,
  useMediaQuery,
  Chip,
  Tooltip,
  ButtonGroup,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  FileUpload as FileUploadIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import { fetchBusinessFeaturePermissions } from '../../../utils/businessPermissions';
import { locationLabel } from '../../../utils/terminology';

interface Location {
  locationId: string;
  locationName: string;
  address: string;
  contactPerson: string;
  contactPhone: string;
  latitude?: number;
  longitude?: number;
  radius?: number;
  assetCount?: number;
}

const EMPTY_FORM = {
  locationName: '',
  address: '',
  contactPerson: '',
  contactPhone: '',
  latitude: '',
  longitude: '',
  radius: '100',
};

const HomeLocationsSettings: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [useSiteTerminology, setUseSiteTerminology] = useState(false);
  const [locations, setLocations] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importValidating, setImportValidating] = useState(false);
  const [importExecuting, setImportExecuting] = useState(false);
  const [validatedData, setValidatedData] = useState<any>(null);
  const [importResults, setImportResults] = useState<any>(null);
  const [duplicateDecisions, setDuplicateDecisions] = useState<Record<number, 'skip' | 'update'>>({});
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const termLabel = locationLabel(useSiteTerminology);
  const termLabelPlural = locationLabel(useSiteTerminology, { plural: true });

  const fetchLocations = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await axios.get(`${API_BASE_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      let rawArray: any[] = [];
      if (response.data?.success && Array.isArray(response.data.data)) {
        rawArray = response.data.data;
      } else if (Array.isArray(response.data)) {
        rawArray = response.data;
      }

      const locationData: Location[] = rawArray.map((item: any) => ({
        locationId: item.locationId || item.location_id || item.id,
        locationName: item.locationName || item.location_name || item.name,
        address: item.address || '',
        contactPerson: item.contactPerson || item.contact_person || '',
        contactPhone: item.contactPhone || item.contact_phone || '',
        latitude:
          typeof item.latitude === 'number'
            ? item.latitude
            : item.latitude
              ? parseFloat(item.latitude)
              : undefined,
        longitude:
          typeof item.longitude === 'number'
            ? item.longitude
            : item.longitude
              ? parseFloat(item.longitude)
              : undefined,
        radius:
          typeof item.radius === 'number'
            ? item.radius
            : item.radius
              ? parseInt(item.radius, 10)
              : undefined,
        assetCount: Number(item.assetCount ?? item.asset_count ?? 0),
      }));

      setLocations(locationData);
      setError(null);
    } catch {
      setError(`Failed to fetch ${locationLabel(useSiteTerminology, { plural: true, lowercase: true })}`);
    } finally {
      setLoading(false);
    }
  }, [useSiteTerminology]);

  useEffect(() => {
    fetchLocations();
    fetchBusinessFeaturePermissions()
      .then((perms) => setUseSiteTerminology(perms.useSiteTerminology))
      .catch(() => {});
  }, [fetchLocations]);

  const handleOpenDialog = () => {
    setSelectedLocation(null);
    setFormData(EMPTY_FORM);
    setOpenDialog(true);
  };

  const handleEditLocation = (location: Location) => {
    setSelectedLocation(location);
    setFormData({
      locationName: location.locationName,
      address: location.address || '',
      contactPerson: location.contactPerson || '',
      contactPhone: location.contactPhone || '',
      latitude: (location.latitude ?? '').toString(),
      longitude: (location.longitude ?? '').toString(),
      radius: (location.radius ?? '100').toString(),
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedLocation(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Please log in to continue');
        return;
      }

      const url = selectedLocation
        ? `${API_BASE_URL}/locations/${selectedLocation.locationId}`
        : `${API_BASE_URL}/locations`;
      const method = selectedLocation ? 'PUT' : 'POST';

      let payload: Record<string, unknown>;
      if (method === 'POST') {
        const businessId = localStorage.getItem('businessId');
        if (!businessId) {
          setError('Business context missing. Please select a business.');
          return;
        }

        const lat = formData.latitude ? parseFloat(formData.latitude) : NaN;
        const lng = formData.longitude ? parseFloat(formData.longitude) : NaN;
        if (!isFinite(lat) || !isFinite(lng)) {
          setError('Please provide valid latitude and longitude.');
          return;
        }

        payload = {
          location_name: formData.locationName,
          latitude: lat,
          longitude: lng,
          radius: formData.radius ? parseInt(formData.radius, 10) : 100,
          business_id: businessId,
          address: formData.address || '',
          description: '',
          contact_person: formData.contactPerson || '',
          contact_phone: formData.contactPhone || '',
        };
      } else {
        payload = {
          locationName: formData.locationName,
          address: formData.address || '',
          contactPerson: formData.contactPerson || '',
          contactPhone: formData.contactPhone || '',
        };
      }

      await axios({
        method,
        url,
        data: payload,
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchLocations();
      handleCloseDialog();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to save ${termLabel.toLowerCase()}`);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/locations/export/template`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'locations_import_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSnackbar({ open: true, message: 'Template downloaded successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to download template', severity: 'error' });
    }
  };

  const handleExportLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/locations/export`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `locations_export_${timestamp}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setSnackbar({ open: true, message: `${termLabelPlural} exported successfully`, severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: `Failed to export ${termLabelPlural.toLowerCase()}`, severity: 'error' });
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportFile(file);
    setImportValidating(true);
    setValidatedData(null);
    setImportResults(null);
    setDuplicateDecisions({});

    try {
      const token = localStorage.getItem('token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/locations/import/validate`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      setValidatedData(response.data.data);
      setSnackbar({
        open: true,
        message: response.data.data.hasErrors ? 'Validation completed with errors' : 'File validated successfully',
        severity: response.data.data.hasErrors ? 'error' : 'success',
      });
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to validate file',
        severity: 'error',
      });
    } finally {
      setImportValidating(false);
      event.target.value = '';
    }
  };

  const handleExecuteImport = async () => {
    if (!validatedData || validatedData.hasErrors) return;

    setImportExecuting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/locations/import/execute`,
        {
          rows: validatedData.rows,
          decisions: duplicateDecisions,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      setImportResults(response.data.data);
      setSnackbar({
        open: true,
        message: `Import completed: ${response.data.data.successCount} successful, ${response.data.data.failureCount} failed`,
        severity: 'success',
      });

      await fetchLocations();
      setImportFile(null);
      setValidatedData(null);
      setDuplicateDecisions({});
    } catch (err: any) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || 'Failed to execute import',
        severity: 'error',
      });
    } finally {
      setImportExecuting(false);
    }
  };

  const handleDuplicateDecision = (rowNumber: number, decision: 'skip' | 'update') => {
    setDuplicateDecisions((prev) => ({
      ...prev,
      [rowNumber]: decision,
    }));
  };

  const handleClearImport = () => {
    setImportFile(null);
    setValidatedData(null);
    setImportResults(null);
    setDuplicateDecisions({});
  };

  const handleCloseImportDialog = () => {
    setImportDialogOpen(false);
    handleClearImport();
  };

  const handleDelete = async (locationId: string) => {
    const location = locations.find((l) => l.locationId === locationId);
    const assetCount = Number(location?.assetCount || 0);
    const term = locationLabel(useSiteTerminology, { lowercase: true });

    if (assetCount > 0) {
      setError(
        `Cannot delete this ${term} because it has ${assetCount} asset${assetCount === 1 ? '' : 's'} assigned. Move or delete those assets first.`,
      );
      return;
    }

    if (!window.confirm(`Are you sure you want to delete this ${term}?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      await axios.delete(`${API_BASE_URL}/locations/${locationId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await fetchLocations();
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to delete ${termLabel.toLowerCase()}`);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {termLabelPlural}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Manage the {termLabelPlural.toLowerCase()} used to assign assets, run location checks, and
        organise files. Each location requires a name, coordinates, and an optional geofence radius.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
        <Button variant="outlined" startIcon={<FileUploadIcon />} onClick={() => setImportDialogOpen(true)}>
          Import
        </Button>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenDialog}>
          Add {termLabel}
        </Button>
      </Box>

      {locations.length === 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 1.5,
            border: '1px solid rgba(0,0,0,0.12)',
            boxShadow: 'none',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No {termLabelPlural.toLowerCase()} yet. Add one to get started.
          </Typography>
        </Paper>
      ) : isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {locations.map((location) => (
            <Paper
              key={location.locationId}
              sx={{ p: 2, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {location.locationName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {location.address || 'No address'}
                  </Typography>
                </Box>
                <Box display="flex" gap={0.5}>
                  <IconButton size="small" onClick={() => handleEditLocation(location)} color="primary">
                    <EditIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(location.locationId)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </Box>
              </Box>
              <Typography variant="body2">
                <strong>Contact:</strong> {location.contactPerson || '—'}
                {location.contactPhone ? ` · ${location.contactPhone}` : ''}
              </Typography>
            </Paper>
          ))}
        </Box>
      ) : (
        <Paper sx={{ borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none', overflow: 'hidden' }}>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{termLabel} Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>Contact Person</TableCell>
                  <TableCell>Contact Phone</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {locations.map((location) => (
                  <TableRow key={location.locationId}>
                    <TableCell>{location.locationName}</TableCell>
                    <TableCell>{location.address}</TableCell>
                    <TableCell>{location.contactPerson}</TableCell>
                    <TableCell>{location.contactPhone}</TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => handleEditLocation(location)} color="primary">
                        <EditIcon />
                      </IconButton>
                      <IconButton size="small" onClick={() => handleDelete(location.locationId)} color="error">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      <Dialog
        open={importDialogOpen}
        onClose={handleCloseImportDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>Import {termLabelPlural}</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 3, mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleDownloadTemplate}>
              Download Template
            </Button>
            <Button variant="outlined" startIcon={<CloudUploadIcon />} onClick={handleExportLocations}>
              Export Existing {termLabelPlural}
            </Button>
          </Box>

          <Alert severity="info" sx={{ mb: 2 }}>
            Required columns: {termLabel} Name, Latitude, and Longitude. Optional columns include Radius
            (meters), Address, Contact Person, and Contact Phone. Duplicate names can be skipped or updated.
          </Alert>

          {!importResults && (
            <Paper sx={{ mb: 2.5, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
              <input
                accept=".xlsx,.xls,.xlsm,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
                style={{ display: 'none' }}
                id="location-import-file-upload"
                type="file"
                onChange={handleFileSelect}
              />
              <label htmlFor="location-import-file-upload">
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' },
                  }}
                >
                  <CloudUploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {importFile ? importFile.name : 'Click to upload Excel or CSV file'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Supports .xlsx, .xls, .xlsm, and .csv files
                  </Typography>
                </Box>
              </label>
            </Paper>
          )}

          {importValidating && (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          )}

          {validatedData && !importResults && (
            <Paper sx={{ p: 3, mb: 2, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Validation Results</Typography>
                <Button onClick={handleClearImport}>Clear</Button>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${validatedData.totalRows}`} />
                <Chip label={`Valid: ${validatedData.validRows}`} color="success" />
                {validatedData.rowsWithErrors > 0 && (
                  <Chip label={`Errors: ${validatedData.rowsWithErrors}`} color="error" />
                )}
                {validatedData.duplicatesFound > 0 && (
                  <Chip label={`Duplicates: ${validatedData.duplicatesFound}`} color="warning" />
                )}
              </Box>

              {validatedData.hasErrors && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  Please fix the errors before importing
                </Alert>
              )}

              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Row</TableCell>
                      <TableCell>{termLabel} Name</TableCell>
                      <TableCell>Latitude</TableCell>
                      <TableCell>Longitude</TableCell>
                      <TableCell>Radius</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>Contact Person</TableCell>
                      <TableCell>Contact Phone</TableCell>
                      <TableCell>Status</TableCell>
                      {validatedData.duplicatesFound > 0 && <TableCell>Action</TableCell>}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {validatedData.rows.map((row: any) => (
                      <TableRow key={row.rowNumber}>
                        <TableCell>{row.rowNumber}</TableCell>
                        <TableCell>{row.data.location_name}</TableCell>
                        <TableCell>{row.data.latitude ?? '—'}</TableCell>
                        <TableCell>{row.data.longitude ?? '—'}</TableCell>
                        <TableCell>{row.data.radius ?? '—'}</TableCell>
                        <TableCell>{row.data.address || '—'}</TableCell>
                        <TableCell>{row.data.contact_person || '—'}</TableCell>
                        <TableCell>{row.data.contact_phone || '—'}</TableCell>
                        <TableCell>
                          {row.errors.length > 0 ? (
                            <Tooltip
                              title={
                                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                  {row.errors.map((err: string, idx: number) => (
                                    <li key={idx}>{err}</li>
                                  ))}
                                </Box>
                              }
                            >
                              <Chip label={`Error (${row.errors.length})`} color="error" size="small" />
                            </Tooltip>
                          ) : row.isDuplicate ? (
                            <Chip label="Duplicate" color="warning" size="small" />
                          ) : (
                            <Chip label="Valid" color="success" size="small" />
                          )}
                        </TableCell>
                        {validatedData.duplicatesFound > 0 && (
                          <TableCell>
                            {row.isDuplicate && (
                              <ButtonGroup size="small">
                                <Button
                                  variant={duplicateDecisions[row.rowNumber] === 'skip' ? 'contained' : 'outlined'}
                                  onClick={() => handleDuplicateDecision(row.rowNumber, 'skip')}
                                >
                                  Skip
                                </Button>
                                <Button
                                  variant={duplicateDecisions[row.rowNumber] === 'update' ? 'contained' : 'outlined'}
                                  onClick={() => handleDuplicateDecision(row.rowNumber, 'update')}
                                >
                                  Update
                                </Button>
                              </ButtonGroup>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {!validatedData.hasErrors && (
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleExecuteImport} disabled={importExecuting}>
                    {importExecuting ? <CircularProgress size={24} /> : 'Execute Import'}
                  </Button>
                </Box>
              )}
            </Paper>
          )}

          {importResults && (
            <Paper sx={{ p: 3, borderRadius: 1.5, border: '1px solid rgba(0,0,0,0.12)', boxShadow: 'none' }}>
              <Typography variant="h6" gutterBottom>
                Import Complete
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <Chip label={`Total: ${importResults.totalRows}`} />
                <Chip label={`Successful: ${importResults.successCount}`} color="success" />
                <Chip label={`Failed: ${importResults.failureCount}`} color="error" />
                <Chip label={`Skipped: ${importResults.skippedCount}`} color="default" />
              </Box>
              <Button variant="contained" onClick={handleClearImport}>
                Import More {termLabelPlural}
              </Button>
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseImportDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>{selectedLocation ? `Edit ${termLabel}` : `Add ${termLabel}`}</DialogTitle>
        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={`${termLabel} Name`}
                  value={formData.locationName}
                  onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Person"
                  value={formData.contactPerson}
                  onChange={(e) => setFormData({ ...formData, contactPerson: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Contact Phone"
                  value={formData.contactPhone}
                  onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  required={!selectedLocation}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  inputProps={{ step: 'any' }}
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  required={!selectedLocation}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Radius (meters)"
                  type="number"
                  value={formData.radius}
                  onChange={(e) => setFormData({ ...formData, radius: e.target.value })}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: 1, borderColor: 'divider', p: 2 }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {selectedLocation ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomeLocationsSettings;
